/**
 * Page-path → field-value hints.
 *
 * A reporter skips an optional field. A field nobody fills is the same as no
 * field, so the point of a hint is to turn "choose the deliverable" into "confirm
 * the deliverable" — the page they are on already implies the answer.
 *
 * The matching is deliberately stricter than `startsWith`. A wrong deliverable is
 * worse than a blank one: a blank field gets triaged, a wrong one gets trusted.
 * So `/orders` matches `/orders/abc-123` but must never match `/orders-archive`.
 *
 * This module is pure and has no DOM dependency — the widget calls it with
 * `location.pathname`, the config loader calls the validator with parsed JSON.
 */

export interface PathHint {
  /** A path prefix, matched on whole segments. `/` is a valid catch-all. */
  path: string;
  /** Field key → value to preselect. Keys must name fields the project declares. */
  values: Record<string, string>;
}

/** The part of a field declaration this module needs; avoids importing the schema. */
export interface HintableField {
  key: string;
  type: 'select' | 'text';
  options?: string[] | undefined;
}

/** Non-empty path segments. `/orders/abc/` and `/orders/abc` both give the same. */
function segmentsOf(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0);
}

/**
 * The pathname of a URL, a path-and-query, or a bare pathname.
 *
 * Origin, query and hash are all ignored, so a caller can pass whatever it has
 * without each call site re-deriving the rule.
 */
export function pathnameOf(value: string): string {
  try {
    return new URL(value).pathname;
  } catch {
    // Not absolute — strip a query and/or fragment off a bare path.
    return value.split(/[?#]/, 1)[0] ?? '';
  }
}

/**
 * The most specific hint matching `location`, or null when none does.
 *
 * "Most specific" is the longest run of matched segments. Two matching hints can
 * only tie by having identical segments, which `findPathHintProblems` rejects at
 * config load — so a tie here cannot happen with validated config.
 */
export function matchPathHint(location: string, hints: readonly PathHint[]): PathHint | null {
  const target = segmentsOf(pathnameOf(location));

  let best: PathHint | null = null;
  let bestLength = -1;

  for (const hint of hints) {
    const segments = segmentsOf(hint.path);
    if (segments.length > target.length) continue;
    if (!segments.every((segment, index) => segment === target[index])) continue;

    if (segments.length > bestLength) {
      best = hint;
      bestLength = segments.length;
    }
  }

  return best;
}

/**
 * Values to preselect for `location`. Empty when nothing matches, which is the
 * normal case for an unlisted page rather than an error.
 *
 * The winning hint's `values` is the whole answer — values from less specific
 * hints are not merged underneath it, so what a hint declares is what a reporter
 * on that path sees.
 */
export function resolvePathHintValues(
  location: string,
  hints: readonly PathHint[],
): Record<string, string> {
  return { ...matchPathHint(location, hints)?.values };
}

export interface PathHintProblem {
  /** Index of the offending hint within `pathHints`. */
  index: number;
  /** The field key at fault, when the problem is about one value. */
  key?: string;
  message: string;
}

/**
 * Everything wrong with a project's hints, checked against its field declaration.
 *
 * This runs at config load rather than at use, because a typo here fails silently
 * in the worst possible way: nothing preselects, which looks exactly like "nobody
 * filled the field in" and goes unnoticed for weeks. A config that is wrong but
 * still boots is the expensive kind.
 */
export function findPathHintProblems(
  hints: readonly PathHint[],
  fields: readonly HintableField[],
): PathHintProblem[] {
  const declared = new Map(fields.map((field) => [field.key, field]));
  const problems: PathHintProblem[] = [];
  const seen = new Map<string, number>();

  hints.forEach((hint, index) => {
    // Compare on segments so `/orders`, `/orders/` and `//orders` are one path.
    const canonical = `/${segmentsOf(hint.path).join('/')}`;
    const first = seen.get(canonical);
    if (first === undefined) {
      seen.set(canonical, index);
    } else {
      problems.push({
        index,
        message: `duplicates the path of hint ${first} ("${hints[first]?.path}") — which one wins is not defined`,
      });
    }

    const keys = Object.keys(hint.values);
    if (keys.length === 0) {
      problems.push({ index, message: `hint for "${hint.path}" preselects nothing` });
    }

    for (const key of keys) {
      const field = declared.get(key);
      if (!field) {
        problems.push({ index, key, message: `"${key}" is not a field declared by this project` });
        continue;
      }

      const value = hint.values[key];
      if (field.type === 'select' && value !== undefined && !field.options?.includes(value)) {
        problems.push({
          index,
          key,
          message: `"${value}" is not one of the options declared for "${key}"`,
        });
      }
    }
  });

  return problems;
}
